import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Sparkles, Bell, BellOff, MapPin, DollarSign, GraduationCap, Award, Briefcase, Building2, Search, ArrowRight, CheckCircle2, Loader2, UserCheck } from 'lucide-react';
import JobCard from '../components/JobCard';

const DEGREES = ['BSW', 'MSW', 'DSW/PhD'];
const US_STATES = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
const LICENSES = ['LBSW', 'LSW', 'LMSW', 'LCSW', 'LICSW', 'LCSW-S', 'LISW', 'LISW-S', 'Other'];
const EMPLOYMENT = [
  { v: 'full-time', l: 'Full-Time' }, { v: 'part-time', l: 'Part-Time' },
  { v: 'prn', l: 'PRN' }, { v: 'contract', l: 'Contract' },
  { v: 'per-diem', l: 'Per Diem' }, { v: 'temporary', l: 'Temporary' },
];
const SETTINGS = [
  { v: 'remote', l: 'Remote' }, { v: 'hybrid', l: 'Hybrid' }, { v: 'in-person', l: 'In-Person' },
];
const SPECIALTIES = [
  'Case Management', 'Hospital/Medical Social Work', 'Home Health', 'Hospice',
  'Behavioral Health/Mental Health', 'School Social Work', 'Child Welfare', 'Veterans Services',
  'Dialysis', 'Nursing Facility/LTC', 'Rehabilitation', 'Corrections', 'Substance Use',
  'Community Social Services', 'Geriatrics', 'Pediatrics', 'Foster Care/Adoption',
];
const FREQUENCIES = [
  { v: 'instant', l: 'As soon as a match is posted' },
  { v: 'daily', l: 'Once a day (daily digest)' },
  { v: 'both', l: 'Both — instant and daily' },
];

const emptyPrefs = {
  degrees: [], licenses: [], specialties: [], employment_types: [], work_settings: [],
  preferred_city: '', preferred_state: '', preferred_zip: '', radius_miles: 50,
  min_salary: '', alerts_enabled: true, alert_frequency: 'both',
};

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-primary/50 hover:text-primary'}`}>
    {children}
  </button>
);

const JobPreferences = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isWelcome = params.get('welcome') === 'true';
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [prefs, setPrefs] = useState(emptyPrefs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasPrefs, setHasPrefs] = useState(false);
  const [alertsActive, setAlertsActive] = useState(true);
  const [optIn, setOptIn] = useState(false);
  const formRef = useRef(null);

  useEffect(() => { document.title = 'My Job Preferences | I Need A Social Worker'; }, []);
  useEffect(() => { if (!authLoading && !isAuthenticated) navigate('/login'); }, [authLoading, isAuthenticated, navigate]);

  const loadMatches = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/job-preferences/matches?limit=30`);
      setMatches(r.data.matches || []);
      setTotal(r.data.total || 0);
      setHasPrefs(r.data.has_preferences);
      setAlertsActive(r.data.alerts_active);
    } catch (e) { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/job-preferences`);
      const p = r.data.job_preferences || {};
      setPrefs({ ...emptyPrefs, ...p, min_salary: p.min_salary ?? '' });
    } catch (e) { /* noop */ }
    await loadMatches();
    try {
      const o = await axios.get(`${API_URL}/me/opportunities-optin`);
      setOptIn(!!o.data.receive_employer_opportunities);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [loadMatches]);
  useEffect(() => { load(); }, [load]);

  const saveOptIn = async (v) => {
    setOptIn(v);
    try {
      await axios.post(`${API_URL}/users/opportunities-optin`, { opt_in: v });
      toast.success(v ? 'Employers can now invite you to featured jobs.' : 'You will no longer receive employer invitations.');
    } catch (e) { toast.error('Could not update setting.'); setOptIn(!v); }
  };

  const toggle = (key, value) => setPrefs((p) => ({
    ...p, [key]: p[key].includes(value) ? p[key].filter((x) => x !== value) : [...p[key], value],
  }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/job-preferences`, { ...prefs, min_salary: prefs.min_salary === '' ? null : Number(prefs.min_salary) });
      toast.success('Job preferences saved.');
      await loadMatches();
      setHasPrefs(true);
    } catch (e) { toast.error('Could not save preferences.'); } finally { setSaving(false); }
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <p className="inline-flex items-center gap-2 text-secondary font-semibold uppercase text-xs tracking-widest"><Sparkles className="w-4 h-4" /> Job Preferences</p>
          <h1 className="font-serif text-3xl font-bold mt-1">Tell us the social work job you want</h1>
          <p className="text-white/80 mt-2">{isWelcome ? "Welcome! Set your preferences and we'll match you to the right roles — and email you the moment a new one is posted." : "We'll match you to roles that fit and alert you when new matching jobs go live."}</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Preferences form */}
        <div ref={formRef} className="bg-white rounded-2xl border p-6" data-testid="job-preferences-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="flex items-center gap-2 mb-2"><GraduationCap className="w-4 h-4 text-primary" /> Degree</Label>
              <div className="flex flex-wrap gap-2">{DEGREES.map((d) => <Chip key={d} active={prefs.degrees.includes(d)} onClick={() => toggle('degrees', d)} testid={`degree-${d}`}>{d}</Chip>)}</div>
            </div>
            <div>
              <Label className="flex items-center gap-2 mb-2"><Award className="w-4 h-4 text-primary" /> License</Label>
              <div className="flex flex-wrap gap-2">{LICENSES.map((l) => <Chip key={l} active={prefs.licenses.includes(l)} onClick={() => toggle('licenses', l)} testid={`license-${l}`}>{l}</Chip>)}</div>
            </div>
          </div>

          <div className="mt-6">
            <Label className="flex items-center gap-2 mb-2"><Briefcase className="w-4 h-4 text-primary" /> Specialties / Fields (choose all that apply)</Label>
            <div className="flex flex-wrap gap-2">{SPECIALTIES.map((s) => <Chip key={s} active={prefs.specialties.includes(s)} onClick={() => toggle('specialties', s)} testid={`specialty-${s}`}>{s}</Chip>)}</div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <Label className="mb-2 block">Employment Type</Label>
              <div className="flex flex-wrap gap-2">{EMPLOYMENT.map((e) => <Chip key={e.v} active={prefs.employment_types.includes(e.v)} onClick={() => toggle('employment_types', e.v)} testid={`employment-${e.v}`}>{e.l}</Chip>)}</div>
            </div>
            <div>
              <Label className="flex items-center gap-2 mb-2"><Building2 className="w-4 h-4 text-primary" /> Work Setting</Label>
              <div className="flex flex-wrap gap-2">{SETTINGS.map((s) => <Chip key={s.v} active={prefs.work_settings.includes(s.v)} onClick={() => toggle('work_settings', s.v)} testid={`setting-${s.v}`}>{s.l}</Chip>)}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4 mt-6">
            <div>
              <Label className="flex items-center gap-1 mb-1"><MapPin className="w-3.5 h-3.5" /> State</Label>
              <select className="w-full border rounded-md h-10 px-3 text-sm bg-white" value={prefs.preferred_state} onChange={(e) => setPrefs((p) => ({ ...p, preferred_state: e.target.value }))} data-testid="pref-state">
                <option value="">Any state</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="mb-1 block">City</Label>
              <Input value={prefs.preferred_city} onChange={(e) => setPrefs((p) => ({ ...p, preferred_city: e.target.value }))} placeholder="e.g. Austin" data-testid="pref-city" />
            </div>
            <div>
              <Label className="mb-1 block">ZIP</Label>
              <Input value={prefs.preferred_zip} onChange={(e) => setPrefs((p) => ({ ...p, preferred_zip: e.target.value }))} placeholder="e.g. 78701" data-testid="pref-zip" />
            </div>
            <div>
              <Label className="flex items-center gap-1 mb-1"><DollarSign className="w-3.5 h-3.5" /> Min Salary</Label>
              <Input type="number" value={prefs.min_salary} onChange={(e) => setPrefs((p) => ({ ...p, min_salary: e.target.value }))} placeholder="e.g. 60000" data-testid="pref-salary" />
            </div>
          </div>

          {/* Alerts */}
          <div className="mt-6 bg-primary/5 border border-primary/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              {prefs.alerts_enabled ? <Bell className="w-5 h-5 text-primary" /> : <BellOff className="w-5 h-5 text-gray-400" />}
              <div>
                <p className="font-semibold text-gray-900">Job match alerts</p>
                <p className="text-sm text-gray-500">Email me when a new job matches these preferences.</p>
              </div>
              <Switch checked={prefs.alerts_enabled} onCheckedChange={(v) => setPrefs((p) => ({ ...p, alerts_enabled: v }))} data-testid="pref-alerts-toggle" />
            </div>
            {prefs.alerts_enabled && (
              <select className="border rounded-md h-10 px-3 text-sm bg-white" value={prefs.alert_frequency} onChange={(e) => setPrefs((p) => ({ ...p, alert_frequency: e.target.value }))} data-testid="pref-frequency">
                {FREQUENCIES.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
              </select>
            )}
          </div>

          {/* Employer invitations opt-in */}
          <div className="mt-4 bg-secondary/5 border border-secondary/15 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="opportunities-optin-row">
            <div className="flex items-center gap-3">
              <UserCheck className={`w-5 h-5 ${optIn ? 'text-secondary' : 'text-gray-400'}`} />
              <div>
                <p className="font-semibold text-gray-900">Employer invitations</p>
                <p className="text-sm text-gray-500">Let verified employers invite you to their featured jobs. Your contact details stay private until you respond.</p>
              </div>
            </div>
            <Switch checked={optIn} onCheckedChange={saveOptIn} data-testid="opportunities-optin-toggle" />
          </div>

          <div className="flex justify-end mt-5">
            <Button onClick={save} disabled={saving} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="save-preferences-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <>Save Preferences & Find Matches <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        </div>

        {/* Matches */}
        <div>
          <h2 className="font-serif text-2xl font-bold text-primary mb-4" data-testid="matches-heading">
            {hasPrefs ? `Your Matches${total ? ` (${total})` : ''}` : 'Your Matches'}
          </h2>

          {loading ? (
            <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…</div>
          ) : !hasPrefs ? (
            <div className="bg-white rounded-2xl border p-10 text-center" data-testid="set-prefs-prompt">
              <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">Set your preferences above to see matching social work jobs.</p>
            </div>
          ) : total === 0 ? (
            <div className="bg-white rounded-2xl border-2 border-secondary/30 p-8 sm:p-10 text-center" data-testid="no-match-screen">
              <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                <Search className="w-7 h-7 text-secondary" />
              </div>
              <h3 className="font-serif text-2xl font-bold text-primary">No Matches Right Now — We're Looking for You.</h3>
              <p className="text-gray-600 mt-3 max-w-xl mx-auto">
                We don't currently have a job that matches your selected preferences. Your job preferences have been saved,
                and we'll notify you when a matching social work position is posted.
              </p>
              {alertsActive && (
                <p className="inline-flex items-center gap-2 mt-4 text-green-700 font-semibold bg-green-50 px-4 py-2 rounded-full" data-testid="alert-active-badge">
                  <CheckCircle2 className="w-5 h-5" /> Your Job Alert Is Active
                </p>
              )}
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                <Button onClick={scrollToForm} variant="outline" data-testid="update-prefs-btn">Update Job Preferences</Button>
                <Button onClick={() => navigate('/jobs')} className="bg-primary hover:bg-primary-hover text-white" data-testid="view-all-jobs-btn">View All Social Work Jobs <ArrowRight className="w-4 h-4 ml-1" /></Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="matches-grid">
              {matches.map((job) => <JobCard key={job.job_id} job={job} showActions={false} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobPreferences;
